import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Package,
  Search,
  ArrowRight,
  Truck,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react";
import "./TrackingTab.css";

export default function TrackingTab({ orders, isAr, labels }) {
  const navigate = useNavigate();
  const [trackingCodeInput, setTrackingCodeInput] = useState("");

  const statusMap = {
    pending: { class: "pending", label: isAr ? "قيد الانتظار" : "Pending" },
    processing: { class: "processing", label: isAr ? "قيد التجهيز" : "Processing" },
    shipped: { class: "shipped", label: isAr ? "تم الشحن" : "Shipped" },
    in_transit: { class: "in_transit", label: isAr ? "في الطريق" : "In transit" },
    delivered: { class: "delivered", label: isAr ? "تم التسليم" : "Delivered" },
    canceled: { class: "canceled", label: isAr ? "ملغي" : "Canceled" },
  };

  function getStatusIcon(status) {
    const s = String(status || "").toLowerCase();
    switch (s) {
      case "delivered":
        return <CheckCircle size={14} />;
      case "in_transit":
        return <Truck size={14} />;
      case "canceled":
        return <AlertCircle size={14} />;
      default:
        return <Clock size={14} />;
    }
  }

  function renderStatusBadge(status) {
    if (!status) {
      return (
        <span className="tracking-status-badge pending">
          {isAr ? "غير متوفر" : "N/A"}
        </span>
      );
    }

    const s = String(status).toLowerCase();
    const config = statusMap[s] || statusMap.pending;

    return (
      <span className={`tracking-status-badge ${config.class}`}>
        {config.label}
      </span>
    );
  }

  function renderShippingSummary(o) {
    const name =
      (isAr
        ? o.shipping_name_ar || o.shipping_name_en
        : o.shipping_name_en || o.shipping_name_ar) ||
      (isAr ? "غير محدد" : "N/A");

    const trackingCode =
      o.tracking_code || o.shipping_tracking_code || "";

    const trackingStatus =
      o.tracking_status || o.shipping_status || "";

    return (
      <div className="tracking-shipping-info">
        <div className="tracking-shipping-name">{name}</div>

        {trackingStatus && (
          <div className="tracking-shipping-status">
            {renderStatusBadge(trackingStatus)}
          </div>
        )}

        {trackingCode && (
          <div className="tracking-shipping-code">
            {isAr ? "كود:" : "Code:"} {trackingCode}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tracking-container" dir={isAr ? "rtl" : "ltr"}>
      <div className="tracking-header">
        <div className="tracking-header-icon">
          <Package size={20} strokeWidth={2} />
        </div>
        <h3 className="tracking-header-title">
          {isAr ? "تتبع شحنة" : "Track shipment"}
        </h3>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          const c = trackingCodeInput.trim();
          if (!c) return;
          navigate(`/track/${encodeURIComponent(c)}`);
        }}
        className="tracking-search-form"
      >
        <input
          type="text"
          className="tracking-input"
          value={trackingCodeInput}
          onChange={(e) => setTrackingCodeInput(e.target.value)}
          placeholder={
            isAr ? "أدخل رقم التتبع" : "Enter tracking code"
          }
        />

        <button
          className="tracking-button"
          disabled={!trackingCodeInput.trim()}
          type="submit"
        >
          <Search size={16} />
          <span>{isAr ? "تتبع الآن" : "Track"}</span>
        </button>
      </form>

      {orders.length === 0 ? (
        <div className="tracking-empty">
          <div className="tracking-empty-icon">
            <Package size={32} strokeWidth={1.5} />
          </div>
          <p className="tracking-empty-text">{labels.noOrders}</p>
        </div>
      ) : (
        <div className="tracking-table-wrapper">
          <div className="tracking-table-scroll">
            <table className="tracking-table">
              <thead>
                <tr>
                  <th>{isAr ? "الطلب" : "Order"}</th>
                  <th>{isAr ? "الشحن" : "Shipping"}</th>
                  <th>{isAr ? "الكود" : "Code"}</th>
                  <th>{isAr ? "الحالة" : "Status"}</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {orders.map((o) => {
                  const trackingCode =
                    o.tracking_code || o.shipping_tracking_code || "";

                  const trackingStatus =
                    o.tracking_status || o.shipping_status || "";

                  return (
                    <tr key={o.id}>
                      <td>
                        <span className="tracking-order-id">#{o.id}</span>
                      </td>

                      <td>{renderShippingSummary(o)}</td>

                      <td>
                        {trackingCode ? (
                          <span className="tracking-code">{trackingCode}</span>
                        ) : (
                          <span className="tracking-code empty">—</span>
                        )}
                      </td>

                      <td>{renderStatusBadge(trackingStatus)}</td>

                      <td style={{ textAlign: isAr ? "left" : "right" }}>
                        {trackingCode ? (
                          <Link
                            to={`/track/${encodeURIComponent(trackingCode)}`}
                            className="tracking-action-btn active"
                            title={isAr ? "تتبع" : "Track"}
                          >
                            <ArrowRight size={14} />
                            <span>{isAr ? "تتبع" : "Track"}</span>
                          </Link>
                        ) : (
                          <button
                            className="tracking-action-btn disabled"
                            disabled
                            type="button"
                          >
                            {isAr ? "لا يوجد" : "N/A"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}